import { Link } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import { useGameStore } from '../store/gameStore'
import AchievementPanel from '../components/Common/AchievementPanel'
import TradingPerformanceDashboard from '../components/Common/TradingPerformanceDashboard'
import Icon from '../components/Common/Icon'
import { evaluateAchievements } from '../engine/Achievements'
import { getChapterCardIds } from '../data/chapters'
import { isChapterUnlocked } from '../data/chapters'

function ReviewPage() {
  const {
    getStatistics,
    getBehaviorPatterns,
    progressTracker,
    unlockedAchievements,
    recordAchievementUnlock,
    isInitialized,
    initialize,
  } = useGameStore()
  const stats = isInitialized ? getStatistics() : null

  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [isInitialized, initialize])

  // 勋章解锁状态
  const unlockedMap = useMemo(() => {
    if (!stats) return {}
    const progressMap = Object.fromEntries(
      Array.from(progressTracker.getAllProgress()).map(([k, v]) => [k, { mastery: v.mastery }])
    )
    const chapterCardIds: Record<number, string[]> = {}
    for (let i = 1; i <= 5; i++) chapterCardIds[i] = getChapterCardIds(i)
    const behavior = getBehaviorPatterns()
    const { status } = evaluateAchievements({
      progress: progressMap as never,
      totalAttempts: stats.totalAttempts,
      correctRate: stats.correctRate,
      averageDecisionTimeMs: behavior.averageDecisionTimeMs,
      chapterCardIds,
      isChapterUnlocked: (id) => isChapterUnlocked(id, progressMap),
    })
    for (const [id, ok] of Object.entries(status)) {
      if (ok && !unlockedAchievements[id]) recordAchievementUnlock(id)
    }
    const map: Record<string, number> = { ...unlockedAchievements }
    for (const [id, ok] of Object.entries(status)) {
      map[id] = ok ? (map[id] ?? Date.now()) : 0
    }
    return map
  }, [stats, progressTracker, getBehaviorPatterns, unlockedAchievements, recordAchievementUnlock])

  if (!stats || stats.totalCards === 0) {
    return (
      <div className="min-h-screen p-8 crt-scanlines">
        <div className="max-w-2xl mx-auto">
          <Link to="/" className="font-pixel-display text-xs text-[color:var(--color-cyan)] hover:text-[color:var(--color-bull)] mb-4 inline-block">
            ◀ 返回
          </Link>
          <h1 className="font-pixel text-xl text-[color:var(--color-gold)] mb-6 pixel-title">
            ► 复盘报告
          </h1>
          <div className="pixel-box p-8 text-center">
            <div className="font-pixel text-base text-[color:var(--color-text-dim)] mb-4">
              暂无数据
            </div>
            <p className="font-pixel-body text-lg text-[color:var(--color-text-dim)] mb-6">
              还没有学习记录
            </p>
            <Link
              to="/"
              className="pixel-button pixel-button-primary"
            >
              ► 开始学习
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const accuracy = Math.round(stats.correctRate * 100)
  const completionRate = stats.totalCards > 0
    ? Math.round((stats.completedCards / stats.totalCards) * 100)
    : 0

  // 给个等级评定
  const getRank = (acc: number): { rank: string; color: string } => {
    if (acc >= 90) return { rank: 'S', color: 'var(--color-gold)' }
    if (acc >= 80) return { rank: 'A', color: 'var(--color-bull)' }
    if (acc >= 70) return { rank: 'B', color: 'var(--color-cyan)' }
    if (acc >= 60) return { rank: 'C', color: 'var(--color-warning)' }
    return { rank: 'D', color: 'var(--color-bear)' }
  }
  const rankInfo = getRank(accuracy)

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="font-pixel-display text-[10px] text-[color:var(--color-cyan)] hover:text-[color:var(--color-bull)] mb-3 inline-block">
          ◀ 返回
        </Link>

        {/* ========== 标题 ========== */}
        <div className="pixel-box pixel-box-bright p-4 mb-4 text-center">
          <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-2 tracking-wider">
            ► 勋章成就
          </div>
          <AchievementPanel unlockedMap={unlockedMap} hideHeader />
        </div>

        {/* ========== 评级 ========== */}
        <div className="pixel-box p-3 mb-4 text-center">
          <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-2 tracking-wider">
            ► 交易纪律评级
          </div>
          <div className="font-pixel text-5xl mb-1" style={{ color: rankInfo.color }}>
            {rankInfo.rank}
          </div>
          <div className="font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
            综合评级
          </div>
        </div>

        {/* ========== 交易战绩趋势图 (Trading Performance) ========== */}
        <TradingPerformanceDashboard />

        {/* ========== 核心数据 ========== */}

        {/* ========== 核心数据 ========== */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="pixel-box p-3 text-center">
            <div className="font-pixel text-[8px] text-[color:var(--color-text-dim)] mb-1">
              已完成卡片
            </div>
            <div className="font-pixel text-2xl text-[color:var(--color-bull)] mb-1">
              {stats.completedCards}
            </div>
            <div className="font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
              完成率 {completionRate}%
            </div>
          </div>
          <div className="pixel-box p-3 text-center">
            <div className="font-pixel text-[8px] text-[color:var(--color-text-dim)] mb-1">
              正确率
            </div>
            <div className="font-pixel text-2xl text-[color:var(--color-gold)] mb-1">
              {accuracy}%
            </div>
            <div className="font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
              共 {stats.totalAttempts} 次尝试
            </div>
          </div>
        </div>

        {/* ========== 进度条 ========== */}
        <div className="pixel-box p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-pixel text-[9px] text-[color:var(--color-cyan)]">
              ► 完成进度
            </span>
            <span className="font-pixel-display text-[10px] text-[color:var(--color-text-bright)]">
              {completionRate}%
            </span>
          </div>
          <div className="pixel-progress">
            <div className="pixel-progress-fill" style={{ width: `${completionRate}%` }} />
          </div>

          <div className="flex items-center justify-between mt-2 mb-2">
            <span className="font-pixel text-[9px] text-[color:var(--color-warning)]">
              ► 正确率
            </span>
            <span className="font-pixel-display text-[10px] text-[color:var(--color-text-bright)]">
              {accuracy}%
            </span>
          </div>
          <div className="pixel-progress">
            <div
              className="pixel-progress-fill"
              style={{
                width: `${accuracy}%`,
                backgroundColor: 'var(--color-gold)',
              }}
            />
          </div>
        </div>

        {/* ========== 薄弱项 ========== */}
        <div className="pixel-box p-3 mb-4">
          <div className="font-pixel text-[9px] text-[color:var(--color-bear)] mb-2 tracking-wider inline-flex items-center gap-1.5">
            <Icon name="warn" size={11} />
            薄弱项分析
          </div>
          {stats.weaknessAreas.length === 0 ? (
            <div className="font-pixel-body text-sm text-[color:var(--color-text-dim)] text-center py-3">
              ► 完成更多卡片后会显示薄弱项分析
            </div>
          ) : (
            <div className="space-y-1">
              {stats.weaknessAreas.map((area, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center py-1.5 px-2 bg-[color:var(--color-bg-elevated)] border-2 border-[color:var(--color-border)]"
                >
                  <span className="font-pixel-body text-sm text-[color:var(--color-text)]">
                    {area.category}
                  </span>
                  <span className="pixel-tag text-[color:var(--color-bear)] border-[color:var(--color-bear)]">
                    {area.errorCount} 次错误
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ========== 建议 ========== */}
        <div className="pixel-box p-3 mb-4 border-[color:var(--color-gold)]">
          <div className="flex items-start gap-2">
            <Icon name="bulb" size={15} className="text-[color:var(--color-gold)] mt-0.5" />
            <div className="flex-1">
              <div className="font-pixel text-[9px] text-[color:var(--color-gold)] mb-1 tracking-wider">
                ► 下一步建议
              </div>
              <p className="font-pixel-body text-sm text-[color:var(--color-text)] leading-snug">
                {completionRate < 30
                  ? '继续完成第一章节的剩余小节，循序渐进打牢基础。'
                  : completionRate < 100
                  ? '坚持完成本章剩余内容，纪律比速度更重要！'
                  : '恭喜完成第一章！准备好后可以挑战第二章（即将推出）。'}
              </p>
            </div>
          </div>
        </div>

        {/* ========== 操作按钮 ========== */}
        <div className="text-center">
          <Link to="/" className="pixel-button pixel-button-primary">
            ◀ 返回首页
          </Link>
        </div>
      </div>
    </div>
  )
}

export default ReviewPage